// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What the keyboard does, said out loud (M3-56, 08 §4.3).
//
// THE GAP THIS CLOSES
// -------------------
// A phone player is taught this machine. The flipper zones glow on their first ball (M3-32), the
// plunger is drawn as a plunger with an arrow on it until they have pulled it once (M3-51), and
// `firstRun.ts` remembers which of those lessons have landed. **A desktop player was taught
// nothing at all** — 08 had a whole section for phone play and none for the keyboard, and the only
// complete description of the controls was a source file.
//
// What that cost is specific and it is not "convenience". The plunger is **held**: how long the
// key is down is the launch power, and NEON NOVA's four lane awards are only collectable in a
// narrow band near the bottom of the range. A player who taps Space fires the weakest shot in the
// game on every ball for ever, is locked out of a scoring feature, and has no way to find out. It
// is the exact twin of the touch *drag*, which this product decided years of design ago that
// nobody guesses.
//
// WHY IT IS GENERATED RATHER THAN WRITTEN
// ---------------------------------------
// Every fact here comes out of `KEY_BINDINGS`, which is the same table the key handler asks. A
// hand-written legend is a second copy of the bindings, and this repository has the scar: the
// flipper geometry lived in a stylesheet as well as in the machine until `client/cabinet.ts` took
// it back. **A legend that drifts is worse than no legend**, because a player who presses what it
// says and gets nothing concludes the machine is broken rather than the label.
//
// So this module holds only what a *guide* knows and the input layer should not: which controls
// read as one row, what a key is called in a sentence, and when the panel is worth showing.
//
// WHY THE KEYCAPS SPLIT IN TWO
// ----------------------------
// `←` and `W` are the same in every language; `Space` and `Enter` are words and become `Espace`
// and `Entrée`. Putting the glyphs through the message bundle would mean eleven keys whose French
// is identical to their English, which is exactly the rot `i18n.test.ts` refuses to allow — so a
// keycap is either a glyph or a word, never both, and a test holds every code to that.

import { KEY_BINDINGS, type ControlId } from '../input/desktop'
import type { MessageKey } from './i18n/en'
import type { Orientation } from './phone'

/**
 * What is printed on one key.
 *
 * **A union rather than two optional fields**, so "exactly one of them is set" is a thing the
 * compiler knows instead of a thing a test hopes. A glyph is language-neutral and goes straight to
 * the screen; a word goes through the translator like every other sentence this client shows.
 */
export type Keycap =
  | { readonly kind: 'glyph'; readonly glyph: string }
  | { readonly kind: 'word'; readonly word: MessageKey }

const glyph = (mark: string): Keycap => ({ kind: 'glyph', glyph: mark })
const word = (key: MessageKey): Keycap => ({ kind: 'word', word: key })

/**
 * Every code the bindings use, and what a player sees on the key.
 *
 * **Complete rather than as-needed**, including the codes no row draws: `caps` is a subset of
 * `codes` today and a test asserts this table covers all of `codes`, so promoting an alternate
 * key into the guide is a one-word change rather than a change plus a keycap somebody forgets.
 */
export const KEYCAPS: Readonly<Record<string, Keycap>> = {
  ArrowLeft: glyph('←'),
  ArrowRight: glyph('→'),
  ArrowUp: glyph('↑'),
  ArrowDown: glyph('↓'),
  ShiftLeft: word('keys.shift'),
  ShiftRight: word('keys.shift'),
  Space: word('keys.space'),
  Enter: word('keys.enter'),
  NumpadEnter: word('keys.enter'),
  Slash: glyph('/'),
  KeyZ: glyph('Z'),
  KeyW: glyph('W'),
  KeyA: glyph('A'),
  KeyS: glyph('S'),
  KeyD: glyph('D'),
  KeyP: glyph('P'),
}

/** What is on that key, or the code itself so an unknown one is visible rather than blank. */
export function keycapFor(code: string): Keycap {
  return KEYCAPS[code] ?? glyph(code)
}

/** One line of the guide. */
export interface GuideRow {
  /** Stable, for a React key and for a test to name a row by. */
  readonly id: string
  /** What the control is called. */
  readonly label: MessageKey
  /** Which bindings this row speaks for. */
  readonly controls: readonly ControlId[]
  /** The keycaps to draw, in order, gathered from those bindings. */
  readonly caps: readonly string[]
  /** Any of them held rather than tapped. */
  readonly held: boolean
  /** The one sentence that turns a key into a skill, where there is one. */
  readonly note?: MessageKey
}

/**
 * How the controls group into rows.
 *
 * **The four nudges are one row and the two flippers are two**, which is not an inconsistency: a
 * player thinks "nudge" and then picks a direction, and thinks "left flipper" as a thing of its
 * own. Four nudge rows would be four fifths of the panel spent on the control a player uses least.
 *
 * Every `ControlId` appears here exactly once, held by a test — a binding this list forgot would be
 * a key that works and is documented nowhere, which is the state the whole task exists to end.
 */
const GROUPS: readonly {
  readonly id: string
  readonly label: MessageKey
  readonly controls: readonly ControlId[]
  readonly note?: MessageKey
}[] = [
  { id: 'flipper-left', label: 'guide.flipper-left', controls: ['leftFlipper'] },
  { id: 'flipper-right', label: 'guide.flipper-right', controls: ['rightFlipper'] },
  {
    id: 'plunger',
    label: 'guide.plunger',
    controls: ['plunger'],
    // **The sentence this whole panel is for.** Everything else on it a player would eventually
    // find by pressing keys; nobody finds a launch power by tapping.
    note: 'guide.plunger-note',
  },
  {
    id: 'nudge',
    label: 'guide.nudge',
    controls: ['nudgeUp', 'nudgeLeft', 'nudgeDown', 'nudgeRight'],
    note: 'guide.nudge-note',
  },
  { id: 'pause', label: 'guide.pause', controls: ['pause'] },
]

/** The bindings, by the name of the control they drive. */
const BINDINGS = new Map(KEY_BINDINGS.map((binding) => [binding.control, binding]))

/**
 * The guide, assembled from the bindings.
 *
 * Built once at module load: it is a pure function of a constant table, and rebuilding it per
 * render would be work done sixty times a second to produce the same five rows.
 */
export const GUIDE_ROWS: readonly GuideRow[] = GROUPS.map((group) => {
  const bindings = group.controls.map((control) => BINDINGS.get(control)).filter((b) => b !== undefined)
  return {
    id: group.id,
    label: group.label,
    controls: group.controls,
    caps: bindings.flatMap((binding) => binding.caps),
    held: bindings.some((binding) => binding.held),
    ...(group.note === undefined ? {} : { note: group.note }),
  }
})

/** What decides whether the panel is drawn at all. */
export interface GuideConditions {
  /** Is there a mouse or a trackpad here? `finePointer()` in the browser. */
  readonly pointerIsFine: boolean
  /** The stage's own shape, measured — not the window's. */
  readonly orientation: Orientation
}

/**
 * Should this player be shown the key guide?
 *
 * **Two conditions, and the second is about room rather than about the device.** A fine pointer is
 * the honest test for "there is a keyboard here" that a browser will answer — 08 §6 forbids
 * sniffing the agent, and `navigator.keyboard` is one browser's idea. Landscape is not a proxy for
 * desktop: it is where a rail beside a machine costs nothing, because a pinball cabinet is a tall
 * narrow object and a wide window has dead space on both sides of it. In portrait the same panel
 * would be taking width away from the playfield to explain the playfield.
 *
 * A phone therefore never sees it in either posture — its primary pointer is coarse — and a desktop
 * window dragged narrow and tall loses it until it is a shape that can hold it.
 */
export function shouldShowKeyGuide(conditions: GuideConditions): boolean {
  return conditions.pointerIsFine && conditions.orientation === 'landscape'
}
